import React, { useState, useEffect } from "react";
import axios from "axios";
import LoginPageImage from "../../Assets/Images/LoginPageImage.png";
import RightTopCovenantLogo from "../../Assets/Images/CovenantDeveloperLogo.png";
import { useNavigate } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import { FaSpinner } from "react-icons/fa";

const Login = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const savedCredentials = JSON.parse(
      localStorage.getItem("rememberedCredentials") || "{}"
    );
    if (savedCredentials.identifier && savedCredentials.rememberMe) {
      setIdentifier(savedCredentials.identifier);
      setPassword(savedCredentials.password || "");
      setRememberMe(true);
    }
  }, []);

  const handleRememberMe = (shouldRemember, userIdentifier, userPassword) => {
    if (shouldRemember) {
      const credentialsToSave = {
        identifier: userIdentifier,
        password: userPassword,
        rememberMe: true,
      };
      localStorage.setItem(
        "rememberedCredentials",
        JSON.stringify(credentialsToSave)
      );
    } else {
      localStorage.removeItem("rememberedCredentials");
    }
  };

  const clearMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!identifier || !password) {
      setError("Please fill in all fields");
      return;
    }

    setIsLoading(true);

    try {
      const loginData = {
        identifier: identifier.trim(),
        password: password,
        userType: "admin",
      };

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/admins/admin-login`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(loginData),
      };

      const response = await axios.request(config);

      console.log("Login successful:", response.data);

      handleRememberMe(rememberMe, identifier, password);

      if (response.data.token) {
        localStorage.setItem("authToken", response.data.token);
      }
      if (response.data.admin) {
        localStorage.setItem(
          "CovenantDevelopersAdminData",
          JSON.stringify(response.data.admin)
        );
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          `Login failed: ${error.response.status}`;
        setError(errorMessage);
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!email || !phoneNumber || !newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);

    try {
      const forgotPasswordData = {
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        newPassword: newPassword,
      };

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/admins/admin-forgot-password`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(forgotPasswordData),
      };

      const response = await axios.request(config);

      console.log("Forgot password successful:", response.data);
      setSuccessMessage(
        "Password reset successful! You can now login with your new password."
      );

      setTimeout(() => {
        setIsForgotPassword(false);
        setEmail("");
        setPhoneNumber("");
        setNewPassword("");
        setConfirmPassword("");
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Forgot password error:", error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          `Password reset failed: ${error.response.status}`;
        setError(errorMessage);
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);

    if (!checked) {
      localStorage.removeItem("rememberedCredentials");
    }
  };

  const toggleForgotPassword = () => {
    setIsForgotPassword(!isForgotPassword);
    clearMessages();
    if (!isForgotPassword) {
      setIdentifier("");
      setPassword("");
      setRememberMe(false);
    } else {
      setEmail("");
      setPhoneNumber("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  return (
    <div className="flex h-screen w-full">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src={LoginPageImage}
          alt="Modern Architecture"
          className="w-full h-full object-fill"
        />
      </div>

      <div className="w-full lg:w-1/2 bg-white flex justify-center items-center px-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img
              src={RightTopCovenantLogo}
              alt="Covenant Developers"
              className="h-14 mx-auto"
            />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {isForgotPassword ? "Reset Password" : "Hi, Welcome Back"}
            </h1>
            <p className="text-gray-600 text-sm">
              {isForgotPassword
                ? "Enter your details to reset your password"
                : "Enter your credentials to continue"}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600 text-sm">{successMessage}</p>
            </div>
          )}

          {!isForgotPassword ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Email Address or Phone Number"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all duration-200 text-gray-900 placeholder-gray-400 bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all duration-200 text-gray-900 placeholder-gray-400 bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    disabled={isLoading}
                    className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Remember Me
                  </span>
                </label>
                <button
                  type="button"
                  onClick={toggleForgotPassword}
                  className="text-sm text-gray-600 hover:text-orange-500 transition-colors"
                  disabled={isLoading}
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 outline-none mt-6 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="flex justify-center items-center gap-2">
                      <FaSpinner className="animate-spin text-white w-5 h-5" />
                      Signing In...
                    </div>
                  </>
                ) : (
                  "Continue"
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all duration-200 text-gray-900 placeholder-gray-400 bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all duration-200 text-gray-900 placeholder-gray-400 bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all duration-200 text-gray-900 placeholder-gray-400 bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all duration-200 text-gray-900 placeholder-gray-400 bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div className="flex items-center justify-start gap-2 py-2 text-gray-600 hover:text-orange-500 text-sm  transition-colors">
                <FaArrowLeftLong />
                <button
                  type="button"
                  onClick={toggleForgotPassword}
                  className=""
                  disabled={isLoading}
                >
                  Back to Login
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 outline-none mt-6 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="flex justify-center items-center gap-2">
                      <FaSpinner className="animate-spin text-white w-5 h-5" />
                      Resetting Password...
                    </div>
                  </>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
